import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-react';
import logo from '../../assets/maxelpay-logo-dark.svg';
import './AuthShell.css';

/*
 * AuthShell — the shell behind sign-in, sign-up, OTP and the password flows.
 *
 * Styled to match the marketing site at dev-new.maxelpay.com, whose /login link
 * points straight at this app. Tokens and component specs are that site's own
 * values; see AuthShell.css for the list.
 *
 * Two widths:
 *   wide    login and register, which have room for the supporting panel
 *   narrow  OTP, forgot password and reset, which are single-purpose
 *
 * The form column is white, so it takes the light-ground lockup
 * (maxelpay-logo-dark.svg). The panel beside it stays dark.
 */

/**
 * The statement panel. Deliberately short: a headline, one line, one trust
 * signal. Its job is to make the page feel like a product rather than a form,
 * and the weight comes from type scale and the brand glow, not from copy.
 */
const AuthPanel = ({ heading, lead }) => (
  <aside className="mp-auth-panel">
    <span className="mp-auth-panel-grid" aria-hidden="true" />
    <div className="mp-auth-panel-inner">
      {/* <span className="mp-auth-eyebrow">MaxelPay dashboard</span> */}
      <h2>{heading}</h2>
      <p>{lead}</p>
      <p className="mp-auth-panel-note">
        <ShieldCheck size={14} />
        Protected by two-factor authentication and device trust
      </p>
    </div>
  </aside>
);

export const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

/**
 * One labelled field. The label is visible and above the input: a placeholder
 * disappears on focus, exactly when someone mid-form wants to check it.
 */
export const AuthField = ({ id, label, error, hint, trailing, ...inputProps }) => (
  <div>
    <label className="mp-auth-label" htmlFor={id}>{label}</label>
    <div className={`mp-auth-field ${error ? 'has-error' : ''}`}>
      <input
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-err` : hint ? `${id}-hint` : undefined}
        {...inputProps}
      />
      {trailing}
    </div>
    {error && <span className="mp-auth-err" id={`${id}-err`}>{error}</span>}
    {/* A hint and an error never show together: two messages about one input
        is one too many. */}
    {!error && hint && <span className="mp-auth-hint" id={`${id}-hint`}>{hint}</span>}
  </div>
);

export const AuthSubmit = ({ label, loading, block = true }) => (
  <button
    type="submit"
    className={`mp-auth-btn is-primary ${block ? 'is-block' : ''}`}
    disabled={loading}
  >
    {loading ? <span className="mp-auth-spin" /> : label}
  </button>
);

/** Error / success banner. `tone` is 'err' or 'ok'. */
export const AuthAlert = ({ tone = 'err', children }) => (
  <div className={`mp-auth-alert is-${tone}`} role={tone === 'err' ? 'alert' : 'status'}>
    {tone === 'err' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
    <span>{children}</span>
  </div>
);

/** The Login / Create account tab pair. Rendered on the wide pages only. */
export const AuthTabs = ({ active }) => (
  <nav className="mp-auth-tabs" aria-label="Sign in or create an account">
    {active === 'login' ? (
      <span className="is-active" aria-current="page">Sign in</span>
    ) : (
      <Link to="/login">Sign in</Link>
    )}
    {active === 'signup' ? (
      <span className="is-active" aria-current="page">Create account</span>
    ) : (
      <Link to="/register">Create account</Link>
    )}
  </nav>
);

/**
 * @param {'wide'|'narrow'} [width]  wide adds the supporting panel.
 * @param {React.ReactNode} [icon]   Tile icon, used by the narrow pages in
 *                                   place of the logo lockup.
 * @param {string} [eyebrow]         Small mono label above the title.
 * @param {React.ReactNode} [panelHeading] Panel headline (wide only).
 * @param {string} [panelLead]       One supporting line under it.
 */
const AuthShell = ({
  width = 'wide',
  icon = null,
  eyebrow = null,
  title,
  subtitle = null,
  panelHeading = null,
  panelLead = null,
  children,
}) => (
  <div className="mp-auth">
    <div className={`mp-auth-card ${width === 'narrow' ? 'is-narrow' : 'is-wide'}`}>
      <div className="mp-auth-body">
        {icon ? (
          <span className="mp-auth-tile">{icon}</span>
        ) : (
          <div className="mp-auth-brand">
            <img src={logo} alt="MaxelPay" />
          </div>
        )}

        {eyebrow && <span className="mp-auth-eyebrow">{eyebrow}</span>}
        <h1 className="mp-auth-title">{title}</h1>
        {subtitle && <p className="mp-auth-sub">{subtitle}</p>}

        {children}
      </div>

      {width === 'wide' && <AuthPanel heading={panelHeading} lead={panelLead} />}
    </div>
  </div>
);

export default AuthShell;
